import {
  LinkButton,
  ResponsiveImage,
  SubsectionTitle,
} from '@sleepstation/components'
import { LinkBase } from '@sleepstation/components-base'
import { CopyParagraph } from '@sleepstation/components-copy'
import { BreakingLayout, VLayout } from '@sleepstation/components-layouts'
import { CopyRenderer } from '@sleepstation/components-markdown-renderer'
import {
  DIRECTION,
  LAYOUT_CHILD,
  LAYOUT_SPACING,
  SIZE,
} from '@sleepstation/const'
import {
  baseline,
  marginBottom,
  marginTop,
  mq,
  padding,
  paddingRight,
  themeColor,
} from '@sleepstation/style-api-helpers'
import { outset } from '@sleepstation/style-mixins'
import { reversedTheme } from '@sleepstation/themes-public-site'
import PropTypes from 'prop-types'
import React from 'react'
import styled, { ThemeProvider } from 'styled-components'
import starsHorizontalVariantSvg from '../../../../../../../../content/images/illustrations/stars/stars-horizonal-variant.svg'
import starsLinkCardSvg from '../../../../../../../../content/images/illustrations/stars/starts-card-variant.svg'

// -----------------------------------------------------------------------------
// Sub-components
// -----------------------------------------------------------------------------

const Title = styled(SubsectionTitle)``

const Wrapper = styled(LinkBase).attrs(({ id }) => ({
  'data-cy': `${id}-card`,
}))`
  text-decoration: none;
`

const Layout = styled(BreakingLayout).attrs(({ id }) => ({
  spacing: [LAYOUT_SPACING.MEDIUM, LAYOUT_SPACING.MEDIUM, LAYOUT_SPACING.LARGE],
  direction: [DIRECTION.VERTICAL, DIRECTION.VERTICAL, DIRECTION.HORIZONTAL],
  child: [LAYOUT_CHILD.FIGHT, LAYOUT_CHILD.FIGHT, LAYOUT_CHILD.FIGHT],
  'data-cy': `${id}-card`,
}))`
  position: relative;
  ${padding([1, 1, 1.25])}
  ${outset(SIZE.LARGE, `background`)}
  background: url(${starsLinkCardSvg}), linear-gradient(${themeColor(
    `backgroundStart`
  )}, ${themeColor(`backgroundEnd`)});
  background-position: top right;
  background-repeat: no-repeat;
  background-size: 231px, 100%;

  ${mq(`mediumUp`)} {
    background: url(${starsHorizontalVariantSvg}),
      linear-gradient(
        ${themeColor(`backgroundStart`)},
        ${themeColor(`backgroundEnd`)}
      );
    background-position: top right;
    background-repeat: no-repeat;
  }
`

const Primary = styled(VLayout).attrs({
  spacing: LAYOUT_SPACING.LARGE,
})`
  ${CopyParagraph} {
    ${baseline(1)}
  }
  ${mq(`mediumUp`)} {
    ${paddingRight(2)}
  }
`

const Secondary = styled(VLayout).attrs({
  spacing: LAYOUT_SPACING.LARGE,
})`
  ${mq(`mediumUp`)} {
    flex-basis: 25%;
    flex-shrink: 0;
    margin-left: auto;
  }
`

const Header = styled(VLayout).attrs({
  as: `header`,
  spacing: LAYOUT_SPACING.MEDIUM,
})``

const Footer = styled.footer`
  > * {
    width: 100%;

    ${mq(`smallUp`)} {
      max-width: 300px;
    }
  }
`

const ImageWrapper = styled.div`
  > * {
    &:first-child {
      width: 200px;
      margin: 0 auto;
      ${marginBottom(-1.55)}
      ${marginTop(0.55)}
      ${mq(`mediumUp`)} {
        display: none;
      }
    }

    &:nth-child(2) {
      display: none;
      max-width: 230px;
      margin-left: auto;
      ${mq(`mediumUp`)} {
        display: block;
      }
    }

    &:only-child {
      ${mq(`mediumUp`)} {
        display: block;
      }
    }
  }
`

// -----------------------------------------------------------------------------
// Component
// -----------------------------------------------------------------------------

const ImageCtaCard = ({
  title,
  id,
  primary,
  secondary,
  link,
  shouldShowLink,
}) => (
  <ThemeProvider theme={reversedTheme}>
    <Wrapper href={link.url} forceOpenInSameWindow>
      <Layout id={id}>
        <Primary>
          <Header>
            <Title>{title}</Title>
            <CopyRenderer htmlAst={primary.bodyAst} />
          </Header>
          {shouldShowLink && (
            <Footer>
              <LinkButton as="div" size={SIZE.SMALL} data-cy="card-next-link">
                {link.label}
              </LinkButton>
            </Footer>
          )}
        </Primary>
        <Secondary>
          {` `}
          <ImageWrapper>
            <ResponsiveImage
              aspectRatio={secondary.image.default.aspectRatio}
              src={secondary.image.default.url}
              alt={secondary.image.default.alt}
            />
            <ResponsiveImage
              aspectRatio={secondary.image.mediumUp.aspectRatio}
              src={secondary.image.mediumUp.url}
              alt={secondary.image.mediumUp.alt}
            />
          </ImageWrapper>
        </Secondary>
      </Layout>
    </Wrapper>
  </ThemeProvider>
)

ImageCtaCard.propTypes = {
  title: PropTypes.string.isRequired,
  id: PropTypes.string.isRequired,
  primary: PropTypes.object.isRequired,
  secondary: PropTypes.object.isRequired,
  link: PropTypes.object.isRequired,
  shouldShowLink: PropTypes.bool,
}

ImageCtaCard.defaultProps = {
  shouldShowLink: true,
}

export default ImageCtaCard
